import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Collapse, Flex, List, Popconfirm, Progress, Result, Space, Statistic, Steps, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useChannelImport } from '../../hooks/useChannelImport';
import { useChannelCategories } from '../../hooks/useChannelCategories';
import ChannelImportLinksForm from '../../components/ChannelImportLinksForm/ChannelImportLinksForm.component';
import ChannelImportReviewTable from '../../components/ChannelImportReviewTable/ChannelImportReviewTable.component';
import { ChannelImportDraftEntity, ChannelImportFailure, ChannelImportReport } from '../../../domain/entities/ChannelImportDraft';

import './AdminChannelImportPage.styles.css';

const STEP_BY_STATUS = { resolving: 1, ready: 2, imported: 3, failed: 1 } as const;

const FailuresList: React.FC<{ failures: ChannelImportFailure[]; titleKey: string }> = ({ failures, titleKey }) => {
  const { t } = useTranslation();
  if (failures.length === 0) return null;

  return (
    <Collapse
      size="small"
      items={[{
        key: 'failures',
        label: t(titleKey, { ns: 'labels', count: failures.length }),
        children: (
          <List
            size="small"
            dataSource={failures}
            rowKey={(failure) => failure.link}
            renderItem={(failure) => (
              <List.Item>
                <Flex vertical>
                  <Typography.Text>{failure.link}</Typography.Text>
                  <Typography.Text type="secondary" className="channel-import-failure-reason">{failure.reason}</Typography.Text>
                </Flex>
              </List.Item>
            )}
          />
        ),
      }]}
    />
  );
};

const ReportStats: React.FC<{ report: ChannelImportReport }> = ({ report }) => {
  const { t } = useTranslation();

  return (
    <Space size={32} wrap>
      <Statistic title={t('channel-import-created', { ns: 'labels' })} value={report.created} />
      <Statistic title={t('channel-import-updated', { ns: 'labels' })} value={report.updated} />
      <Statistic title={t('channel-import-failed', { ns: 'labels' })} value={report.failed} />
    </Space>
  );
};

const AdminChannelImportPage: React.FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const draftUuid = searchParams.get('draft') ?? undefined;
  const { draft, rows, isDirty, preview, accounts, recent, isLoading, isSaving, error, start, setRows, runPreview, apply } = useChannelImport(draftUuid);
  const { categories } = useChannelCategories();

  const categoryNames = useMemo(() => categories.map((category) => category.name), [categories]);
  const currentStep = draft ? STEP_BY_STATUS[draft.status] : 0;

  const translateError = (slug: string) => t(slug, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) });
  const run = async (action: () => Promise<unknown>) => {
    try {
      await action();
      return true;
    } catch (e) {
      message.error(translateError(e instanceof Error ? e.message : 'unknown-error'));
      return false;
    }
  };

  const openDraft = (uuid?: string) => setSearchParams(uuid ? { draft: uuid } : {});

  const handleStart = async (rawLinks: string, telegramAccountUuid: string) => {
    await run(async () => openDraft(await start(rawLinks, telegramAccountUuid)));
  };

  const handleApply = async () => {
    if (await run(apply)) message.success(t('channel-import-done', { ns: 'messages' }));
  };

  const renderResolving = (current: ChannelImportDraftEntity) => (
    <Flex vertical gap={16} align="center" className="channel-import-progress">
      {current.status === 'failed' ? (
        <Result
          status="error"
          title={t('channel-import-resolve-failed', { ns: 'titles' })}
          subTitle={current.errorMessage}
          extra={<Button type="primary" onClick={() => openDraft()}>{t('channel-import-new', { ns: 'buttons' })}</Button>}
        />
      ) : (
        <>
          <Progress type="circle" percent={current.progressPercent} />
          <Typography.Text>
            {t('channel-import-progress', { ns: 'labels', processed: current.processedLinks, total: current.totalLinks })}
          </Typography.Text>
          <Typography.Text type="secondary">{t('channel-import-progress-hint', { ns: 'labels' })}</Typography.Text>
        </>
      )}
    </Flex>
  );

  const renderReview = (current: ChannelImportDraftEntity) => (
    <Flex vertical gap={16}>
      <Space size={32} wrap>
        <Statistic title={t('channel-import-rows', { ns: 'labels' })} value={rows.length} />
        <Statistic title={t('channel-import-rows-existing', { ns: 'labels' })} value={rows.filter((row) => row.exists).length} />
        <Statistic
          title={t('channel-import-rows-review', { ns: 'labels' })}
          value={rows.filter((row) => !row.typeResolved).length}
          valueStyle={rows.some((row) => !row.typeResolved) ? { color: '#d48806' } : undefined}
        />
        <Statistic title={t('channel-import-rows-failed', { ns: 'labels' })} value={current.failures.length} />
      </Space>

      {rows.some((row) => !row.typeResolved) && (
        <Alert type="warning" showIcon message={t('channel-import-review-hint', { ns: 'labels' })} />
      )}

      <FailuresList failures={current.failures} titleKey="channel-import-resolve-failures" />

      <ChannelImportReviewTable rows={rows} categoryNames={categoryNames} disabled={isSaving} onChange={setRows} />

      {preview && (
        <Alert
          type="info"
          showIcon
          message={t('channel-import-preview-title', { ns: 'labels' })}
          description={<ReportStats report={preview} />}
        />
      )}

      <Flex gap={8} justify="flex-end" wrap="wrap" className="channel-import-actions">
        {isDirty && <Typography.Text type="secondary">{t('channel-import-unsaved', { ns: 'labels' })}</Typography.Text>}
        <Button onClick={() => run(runPreview)} loading={isSaving} disabled={rows.length === 0}>
          {t('channel-import-preview', { ns: 'buttons' })}
        </Button>
        <Popconfirm
          title={t('channel-import-apply-confirm', { ns: 'modals', count: rows.length })}
          okText={t('channel-import-apply', { ns: 'buttons' })}
          cancelText={t('cancel', { ns: 'buttons' })}
          onConfirm={handleApply}
        >
          <Button type="primary" loading={isSaving} disabled={rows.length === 0}>{t('channel-import-apply', { ns: 'buttons' })}</Button>
        </Popconfirm>
      </Flex>
    </Flex>
  );

  const renderDone = (current: ChannelImportDraftEntity) => (
    <Result
      status={current.report && current.report.failed > 0 ? 'warning' : 'success'}
      title={t('channel-import-finished', { ns: 'titles' })}
      subTitle={current.report && <ReportStats report={current.report} />}
      extra={[
        <Button key="channels" type="primary" onClick={() => navigate('/admin/channels')}>{t('channel-import-to-channels', { ns: 'buttons' })}</Button>,
        <Button key="new" onClick={() => openDraft()}>{t('channel-import-new', { ns: 'buttons' })}</Button>,
      ]}
    >
      {current.report && <FailuresList failures={current.report.failures} titleKey="channel-import-apply-failures" />}
    </Result>
  );

  return (
    <div className="channel-import-page">
      <div>
        <h1 className="channel-import-title-heading">{t('channel-import', { ns: 'titles' })}</h1>
        <p className="channel-import-subtitle">{t('channel-import-subtitle', { ns: 'titles' })}</p>
      </div>

      <Steps
        current={currentStep}
        status={draft?.status === 'failed' ? 'error' : undefined}
        responsive
        items={[
          { title: t('channel-import-step-links', { ns: 'labels' }) },
          { title: t('channel-import-step-resolve', { ns: 'labels' }) },
          { title: t('channel-import-step-review', { ns: 'labels' }) },
          { title: t('channel-import-step-done', { ns: 'labels' }) },
        ]}
      />

      {error && !draft && <Alert type="error" showIcon message={translateError(error)} />}

      {!draft && !isLoading && (
        <ChannelImportLinksForm accounts={accounts} recent={recent} isSaving={isSaving} onStart={handleStart} onOpenDraft={openDraft} />
      )}
      {draft && (draft.isResolving || draft.status === 'failed') && renderResolving(draft)}
      {draft?.isEditable && renderReview(draft)}
      {draft?.status === 'imported' && renderDone(draft)}
    </div>
  );
};

export default AdminChannelImportPage;
